package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.width
import dev.lingvo.shared.AddProjectLocaleRequest
import dev.lingvo.shared.COMMON_LOCALES
import dev.lingvo.shared.CreateFileRequest
import dev.lingvo.shared.FileFormat
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LFilesIcon
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.ds.LPlusIcon
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTable
import dev.lingvo.site.ds.LTableBody
import dev.lingvo.site.ds.LTableCell
import dev.lingvo.site.ds.LTableHead
import dev.lingvo.site.ds.LTableHeaderCell
import dev.lingvo.site.ds.LTableRow
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LTrashIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
internal fun ProjectLanguageSettings(
    project: ProjectDetailDto,
    slug: String,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    val available = COMMON_LOCALES.filter { it.code != project.sourceLocale && it.code !in project.locales }
    // **`project.locales` includes the source.** `ProjectRepo.create` writes
    // `listOf(sourceLocale) + targetLocales`, so a table captioned "Target languages" listed the
    // project's source language with a live DANGER "Remove" beside it, and a modal promising to
    // permanently delete its translations and glossary entries — for a request the server answers
    // `400 "The source locale cannot be removed."`. Every sibling surface already filters it out,
    // and the add picker three lines above does too. With it gone the "No target languages" empty
    // state below is reachable for the first time, which is when it was ever meant to appear.
    val targets = project.locales.filterNot { it == project.sourceLocale }
    var locale by remember(project.locales) { mutableStateOf(available.firstOrNull()?.code.orEmpty()) }
    var removing by remember { mutableStateOf<String?>(null) }
    var removingBusy by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    val addLocale: () -> Unit = {
        scope.launch {
            busy = true
            // `finally`, because the helper rethrows cancellation: a trailing `busy = false` is
            // skipped on that path and the control it gates would stay disabled.
            try {
                settingsOperationResult { ApiClient.addProjectLocale(slug, AddProjectLocaleRequest(locale)) }
                    .onSuccess {
                        notify(
                            trf(
                                "app.projects.slug.settings.project_structure_settings.language_added",
                                "{language} added",
                                "language" to localeInfo(locale).englishName,
                            ),
                            LToastVariant.SUCCESS,
                            4_000,
                        )
                        onChanged()
                    }
                    .onFailure {
                        notify(
                            it.safeMessage(
                                tr(
                                    "app.projects.slug.settings.project_structure_settings.couldnt_add_this_language_retry",
                                    "Couldn't add this language. Retry?",
                                ),
                            ),
                            LToastVariant.DANGER,
                            TOAST_UNTIL_DISMISSED,
                        )
                    }
            } finally {
                busy = false
            }
        }
    }
    SettingsSection(
        title = t("app.projects.slug.settings.project_structure_settings.languages", "Languages"),
        description = t(
            "app.projects.slug.settings.project_structure_settings.add_target_languages_or_remove_them",
            "Add target languages or remove them with their translation data.",
        ),
        icon = { LLanguagesIcon() },
    ) {
        Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.Bottom) {
            LSelect(
                locale,
                available.map { LSelectOption(it.code, "${localeFlagPrefix(it.code)}${it.englishName}") },  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                { locale = it },
                Modifier.width(22.cssRem),
                t("app.projects.slug.settings.project_structure_settings.target_locale", "Target locale"),
                enabled = available.isNotEmpty() && !busy,
            )
            LButton(
                t("app.projects.slug.settings.project_structure_settings.add_language", "Add language"),
                addLocale,
                enabled = locale.isNotBlank() && !busy,
                // `busy` already disabled the button and said nothing else, so adding a language to
                // a large project looked like a click that had not registered.
                loading = busy,
                leadingIcon = { LPlusIcon() },
            )
        }
        if (targets.isEmpty()) {
            LEmptyState(
                title = t("app.projects.slug.settings.project_structure_settings.no_target_languages", "No target languages"),
                description = t("app.projects.slug.settings.project_structure_settings.add_the_first_language_you_want", "Add the first language you want to translate."),
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.LANGUAGE,
            ) {
                LButton(
                    tf(
                        "app.projects.slug.settings.project_structure_settings.add_named_language",
                        "Add {language}",
                        "language" to (
                            locale.takeIf(String::isNotBlank)?.let(::localeInfo)?.englishName
                                ?: t("app.projects.slug.settings.project_structure_settings.language_placeholder", "language")
                        ),
                    ),
                    addLocale,
                    // **Secondary, and gated by the same state as the control it mirrors.** This is
                    // the picker's own `addLocale`, drawn a few pixels below the picker's button: as
                    // a second accent primary the screen offered the same action twice in one glance,
                    // and — with no `enabled`/`loading` of its own — the lower one stayed pressable
                    // while the upper one was busy, so a double press sent two add requests. The
                    // Files empty state forty lines below already stands in this exact relation to
                    // its own picker, and is secondary for the same reason.
                    variant = LButtonVariant.SECONDARY,
                    enabled = locale.isNotBlank() && !busy,
                    leadingIcon = { LPlusIcon() },
                    loading = busy,
                )
            }
        } else {
            LTable(caption = t("app.projects.slug.settings.project_structure_settings.target_languages", "Target languages")) {
                LTableHead {
                    LTableRow {
                        // Column headings a screen reader repeats for every cell in the row. They
                        // were a `listOf` of bare English, which the extractor cannot reach.
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_language", "Language"))
                        }
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_locale", "Locale"))
                        }
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_action", "Action"))
                        }
                    }
                }
                LTableBody {
                    targets.forEach { code ->
                        LTableRow {
                            LTableCell { Text("${localeFlagPrefix(code)}${localeInfo(code).englishName}") }  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                            LTableCell { Span { Text(code) } }
                            LTableCell {
                                LButton(
                                    t("app.projects.slug.settings.project_structure_settings.remove", "Remove"),
                                    { removing = code },
                                    variant = LButtonVariant.DANGER,
                                    size = LButtonSize.SM,
                                    leadingIcon = { LTrashIcon() },
                                    // Every row's button reads "Remove"; a screen reader's list of
                                    // them is otherwise twelve identical entries.
                                    accessibleLabel = trf(
                                        "app.projects.slug.settings.project_structure_settings.remove_named_language",
                                        "Remove {language}",
                                        "language" to localeInfo(code).englishName,
                                    ),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    // The confirm carries the request, the way `Webhooks.kt` and `Keys.kt` do theirs. It used to
    // set the pending state to null and launch, so the dialog vanished the instant it was pressed
    // and the deletion of every translation in a language happened behind a screen that had already
    // moved on.
    //
    // **`LConfirmDialog`, not a hand-rolled `LModal` with two buttons.** The sibling delete twenty
    // lines below — the same tier of destruction, in the same file — already used it, so this
    // surface shipped two different confirmations for two indistinguishable decisions: one with a
    // busy label and a focus return after a failure, one without.
    //
    // The number was already in hand: `project.progress` carries a per-locale `translated` count,
    // and "its translations" is the vaguest possible way to describe losing 812 of them. One whole
    // sentence with named holes rather than three concatenated fragments — a translator handed
    // "Removing X permanently deletes " cannot put the pieces in their own order.
    val written = removing?.let { code -> project.progress.firstOrNull { it.locale == code } }?.translated ?: 0
    LConfirmDialog(
        open = removing != null,
        title = t("app.projects.slug.settings.project_structure_settings.remove_target_language", "Remove target language?"),
        confirmLabel = t("app.projects.slug.settings.project_structure_settings.remove_language", "Remove language"),
        onConfirm = {
            val target = removing ?: return@LConfirmDialog
            scope.launch {
                removingBusy = true
                try {
                    settingsOperationResult { ApiClient.removeProjectLocale(slug, target) }
                        .onSuccess {
                            notify(
                                trf(
                                    "app.projects.slug.settings.project_structure_settings.language_removed",
                                    "{language} removed",
                                    "language" to localeInfo(target).englishName,
                                ),
                                LToastVariant.SUCCESS,
                                4_000,
                            )
                            onChanged()
                        }
                        .onFailure {
                            notify(
                                it.safeMessage(
                                    tr(
                                        "app.projects.slug.settings.project_structure_settings.couldnt_remove_this_language_retry",
                                        "Couldn't remove this language. Retry?",
                                    ),
                                ),
                                LToastVariant.DANGER,
                                TOAST_UNTIL_DISMISSED,
                            )
                        }
                } finally {
                    removingBusy = false
                    removing = null
                }
            }
        },
        onCancel = { removing = null },
        consequenceTitle = t(
            "app.projects.slug.settings.project_structure_settings.translation_data_will_be_deleted",
            "Translation data will be deleted",
        ),
        consequence = tf(
            "app.projects.slug.settings.project_structure_settings.removing_deletes_its_data",
            "Removing {language} permanently deletes {translations}, and this language's " +
                "glossary entries. This cannot be undone.",
            "language" to removing?.let(::localeInfo)?.englishName.orEmpty(),
            "translations" to tPlural(
                "app.projects.slug.settings.project_structure_settings.count.translation",
                written,
                "the {count} translation written in it",
                "the {count} translations written in it",
            ),
        ),
        busy = removingBusy,
        busyLabel = t(
            "app.projects.slug.settings.project_structure_settings.removing_language",
            "Removing language…",
        ),
    )
}

@Composable
internal fun ProjectFileSettings(
    project: ProjectDetailDto,
    slug: String,
    onChanged: () -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    val scope = rememberCoroutineScope()
    var path by remember { mutableStateOf("") }
    var format by remember { mutableStateOf(FileFormat.JSON_NESTED.name) }
    var deleting by remember { mutableStateOf<String?>(null) }
    var deletingBusy by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }
    SettingsSection(
        title = t("app.projects.slug.settings.project_structure_settings.files", "Files"),
        description = t(
            "app.projects.slug.settings.project_structure_settings.define_the_localization_files_that_belong",
            "Define the localization files that belong to this project.",
        ),
        icon = { LFilesIcon() },
    ) {
        Row(Modifier.fillMaxWidth().gap(Space.SM).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.Bottom) {
            LTextField(
                path,
                { path = it },
                Modifier.width(24.cssRem),
                t("app.projects.slug.settings.project_structure_settings.file_path_field", "File path"),
                // Not copy: a file path in the repository, identical in every language.
                "web/locales/en.json",  // i18n-ignore — an example repository path, not a sentence
            )
            LSelect(
                format,
                FileFormat.entries.map { LSelectOption(it.name, it.displayName) },
                { format = it },
                Modifier.width(16.cssRem),
                t("app.projects.slug.settings.project_structure_settings.format", "Format"),
            )
            LButton(
                label = t("app.projects.slug.settings.project_structure_settings.add_file", "Add file"),
                onClick = {
                    scope.launch {
                        busy = true
                        try {
                            settingsOperationResult { ApiClient.createFile(slug, CreateFileRequest(path.trim(), FileFormat.valueOf(format))) }
                                .onSuccess {
                                    path = ""
                                    notify(
                                        tr("app.projects.slug.settings.project_structure_settings.file_added", "File added"),
                                        LToastVariant.SUCCESS,
                                        4_000,
                                    )
                                    onChanged()
                                }
                                .onFailure {
                                    notify(
                                        it.safeMessage(
                                            tr(
                                                "app.projects.slug.settings.project_structure_settings.couldnt_add_this_file_retry",
                                                "Couldn't add this file. Retry?",
                                            ),
                                        ),
                                        LToastVariant.DANGER,
                                        TOAST_UNTIL_DISMISSED,
                                    )
                                }
                        } finally {
                            busy = false
                        }
                    }
                },
                enabled = path.isNotBlank() && !busy,
                loading = busy,
                leadingIcon = { LPlusIcon() },
            )
        }
        if (project.files.isEmpty()) {
            LEmptyState(
                title = t("app.projects.slug.settings.project_structure_settings.no_localization_files", "No localization files"),
                description = t("app.projects.slug.settings.project_structure_settings.add_a_source_file_to_start", "Add a source file to start organizing keys."),
                modifier = Modifier.fillMaxWidth(),
                icon = LEmptyStateIcon.FILES,
            ) {
                LButton(
                    t("app.projects.slug.settings.project_structure_settings.use_a_starter_path", "Use a starter path"),
                    { path = "locales/${project.sourceLocale}.json"; format = FileFormat.JSON_NESTED.name },
                    variant = LButtonVariant.SECONDARY,
                )
            }
        } else {
            LTable(caption = t("app.projects.slug.settings.project_structure_settings.project_files", "Project files")) {
                LTableHead {
                    LTableRow {
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_path", "Path"))
                        }
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_format", "Format"))
                        }
                        LTableHeaderCell {
                            Text(t("app.projects.slug.settings.project_structure_settings.column_action", "Action"))
                        }
                    }
                }
                LTableBody {
                    project.files.forEach { file ->
                        LTableRow {
                            LTableCell { Text(file.path) }
                            LTableCell { LBadge(file.format.displayName) }
                            LTableCell {
                                LButton(
                                    t("app.projects.slug.settings.project_structure_settings.delete", "Delete"),
                                    { deleting = file.id },
                                    variant = LButtonVariant.DANGER,
                                    size = LButtonSize.SM,
                                    leadingIcon = { LTrashIcon() },
                                    accessibleLabel = trf(
                                        "app.projects.slug.settings.project_structure_settings.delete_named_file",
                                        "Delete {path}",
                                        "path" to file.path,
                                    ),
                                )
                            }
                        }
                    }
                }
            }
        }
    }
    val pending = project.files.firstOrNull { it.id == deleting }
    // **Friction that tracks the consequence.** `keys.file_id` is `ON DELETE CASCADE`
    // (V1__init.sql:64), so this destroys every key in the file and every translation of them in
    // every language — most of a project, for a large file — and it sat behind one click while
    // deleting the project it lives in asks you to type a slug. Same tier, same component; the
    // confirm also carries the request now, instead of closing the dialog and launching behind it.
    LConfirmDialog(
        open = deleting != null,
        title = t("app.projects.slug.settings.project_structure_settings.delete_file_2", "Delete file?"),
        confirmLabel = t("app.projects.slug.settings.project_structure_settings.delete_file", "Delete file"),
        onConfirm = {
            val id = deleting ?: return@LConfirmDialog
            scope.launch {
                deletingBusy = true
                try {
                    settingsOperationResult { ApiClient.deleteFile(slug, id) }
                        .onSuccess {
                            notify(
                                tr("app.projects.slug.settings.project_structure_settings.file_deleted", "File deleted"),
                                LToastVariant.SUCCESS,
                                4_000,
                            )
                            onChanged()
                        }
                        .onFailure {
                            notify(
                                it.safeMessage(
                                    tr(
                                        "app.projects.slug.settings.project_structure_settings.couldnt_delete_this_file_retry",
                                        "Couldn't delete this file. Retry?",
                                    ),
                                ),
                                LToastVariant.DANGER,
                                TOAST_UNTIL_DISMISSED,
                            )
                        }
                } finally {
                    deletingBusy = false
                    deleting = null
                }
            }
        },
        onCancel = { deleting = null },
        // "can permanently remove" was a hedge the database does not share: every key in this file
        // and every translation under those keys goes, in every language, without exception. A
        // confirmation that understates what it destroys is worse than none — it is the reason
        // somebody clicks it.
        consequenceTitle = t(
            "app.projects.slug.settings.project_structure_settings.every_key_in_this_file_will_be_deleted",
            "Every key in this file will be deleted",
        ),
        // `FileDto.keyCount` was already on the wire and rendered two files away on the overview;
        // "its keys" is what you say when you have not looked.
        consequence = tf(
            "app.projects.slug.settings.project_structure_settings.deleting_removes_its_keys",
            "Deleting {path} permanently removes {keys} and every translation of them, in " +
                "every language. This cannot be undone.",
        "path" to pending?.path.orEmpty(), "keys" to tPlural(
                    "app.projects.slug.settings.project_structure_settings.count.key",
                    pending?.keyCount ?: 0,
                    "its {count} key",
                    "all {count} of its keys",
                )),
        typeToConfirm = pending?.path,
        typeToConfirmLabel = t("app.projects.slug.settings.project_structure_settings.file_path", "File path"),
        busy = deletingBusy,
    )
}

/**
 * The server's sentence, or this page's own — never an internal one.
 *
 * Cast first, the way `Keys.kt` and `Glossary.kt` do: a `check`/`require` failure inside a
 * mutation lambda is a `Throwable` with a message written for a developer, and the unguarded form
 * put "Required value was null." in front of the reader.
 */
private fun Throwable.safeMessage(fallback: String): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank) ?: fallback
